import React from 'react';

import { PALETTE } from './constants';
import { DashboardLoading, daysSince } from './dashboard/dashboardFormatting';
import FocusTable from './dashboard/FocusTable';
import HBars from './dashboard/HBars';
import RadioDashboardSkeleton from './dashboard/RadioDashboardSkeleton';
import RadioDonut from './dashboard/RadioDonut';
import { useGeneralRisk } from './hooks/useGeneralRisk';

// IB-677 rework — Assumptions dashboard. Profile = verification split; key metric =
// verification ageing; focus table = unverified assumptions older than 90 days.
// Assumptions carry no RAG or actions, so those tile positions render "—".
export default function GeneralRiskDashboardAssumptions() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const assumptions = dashboardData?.assumptions || {};
    const overview = dashboardData?.overview || {};
    const overdue90 = assumptions.unverified_over_90_days || [];
    const count = assumptions.count ?? 0;
    const unverified = assumptions.unverified_count ?? 0;
    const verified = Math.max(count - unverified, 0);
    const over90 = overdue90.length;
    const unverifiedUnder90 = Math.max(unverified - over90, 0);
    const buckets = assumptions.unverified_age_buckets || {};

    const tiles = [
        { lead: true, label: $t('Open assumptions'), value: count },
        { label: $t('Unverified'), value: unverified },
        { tone: 'red', label: $t('Unverified > 90 days'), value: over90 },
        { tone: 'warn', label: $t('Overdue actions'), value: '—' },
        { label: $t('Verified'), value: verified },
    ];

    const profile = {
        title: $t('Verification profile'),
        content: (
            <RadioDonut
                total={`${count} ${$t('open')}`}
                segments={[
                    { value: verified, colour: PALETTE.green, label: `${$t('Verified')} · ${verified}` },
                    { value: unverifiedUnder90, colour: PALETTE.blue50, label: `${$t('Unverified')} · ${unverifiedUnder90}` },
                    { value: over90, colour: PALETTE.red, label: `${$t('Unverified > 90 days')} · ${over90}` },
                ]}
            />
        ),
    };

    const keyMetric = {
        title: $t('Verification ageing'),
        content: (
            <HBars
                labelWidth={64}
                rows={[
                    { label: $t('0–30d'), segments: [{ value: buckets['0-30'] ?? 0, colourClass: 'tw-bg-blue-50' }] },
                    { label: $t('31–90d'), segments: [{ value: buckets['31-90'] ?? 0, colourClass: 'tw-bg-blue' }] },
                    { label: $t('>90d'), segments: [{ value: buckets['90+'] ?? 0, colourClass: 'tw-bg-red' }] },
                ]}
                emptyLabel={$t('Nothing unverified')}
            />
        ),
    };

    const focus = (
        <FocusTable
            title={$t('Unverified > 90 days')}
            columns={[$t('Assumption'), $t('Age')]}
            rows={overdue90.map((item) => [item.description || item.id, `${daysSince(item.created_at) ?? 0} ${$t('days')}`])}
            empty={$t('No unverified assumptions older than 90 days')}
        />
    );

    return (
        <RadioDashboardSkeleton
            tiles={tiles}
            profile={profile}
            movement={{ data: overview.movement, category: 'assumptions' }}
            keyMetric={keyMetric}
            focus={focus}
        />
    );
}
